import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import clientService from '../../../services/clientService';
import clientSiteService from '../../../services/clientSiteService';
import clientRequirementsService from '../../../services/clientRequirementsService';
import AddClientModal from './AddClientModal';
import AddClientSiteModal from '../../client-site-management/components/AddClientSiteModal';
import CreateRequirementModal from '../../client-requirements-matching/components/CreateRequirementModal';

const ClientDetailView = ({ client, onClose, onClientUpdated }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [clientData, setClientData] = useState(client);
  const [sites, setSites] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [showRequirementModal, setShowRequirementModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    if (client) {
      loadClientDetails();
      loadSites();
      loadRequirements();
      loadClients();
    }
  }, [client]);

  const loadClientDetails = async () => {
    try {
      const result = await clientService.getClient(client.id);
      if (result?.success) {
        setClientData(result.data);
      }
    } catch (error) {
      console.error('Error loading client details:', error);
    }
  };

  const loadSites = async () => {
    try {
      setLoading(true);
      const result = await clientSiteService.getClientSites({ client_id: client.id });
      if (result?.success) {
        setSites(result.data || []);
      }
    } catch (error) {
      console.error('Error loading sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRequirements = async () => {
    try {
      const result = await clientRequirementsService.getRequirements({ client_id: client.id });
      if (result?.success) {
        setRequirements(result.data || []);
      }
    } catch (error) {
      console.error('Error loading requirements:', error);
    }
  };

  const loadClients = async () => {
    try {
      const result = await clientService.getClients({ status: 'active' });
      if (result?.success) {
        setClients(result.data || []);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const handleEditClient = () => {
    setShowEditModal(true);
  };

  const handleClientSaved = () => {
    setShowEditModal(false);
    loadClientDetails();
    onClientUpdated?.();
  };

  const handleAddSite = () => {
    setEditingSite(null);
    setShowSiteModal(true);
  };

  const handleEditSite = (site) => {
    setEditingSite(site);
    setShowSiteModal(true);
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm('Are you sure you want to delete this site?')) return;
    
    try {
      const result = await clientSiteService.deleteClientSite(siteId);
      if (result?.success) {
        loadSites();
      }
    } catch (error) {
      console.error('Error deleting site:', error);
    }
  };

  const handleSiteSaved = () => {
    setShowSiteModal(false);
    setEditingSite(null);
    loadSites();
  };

  const handleAddRequirement = () => {
    setShowRequirementModal(true);
  };

  const handleRequirementCreated = () => {
    setShowRequirementModal(false);
    loadRequirements();
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      inactive: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
      open: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      filled: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
    };
    return colors[status] || colors.inactive;
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'sites', label: 'Sites', icon: 'MapPin' },
    { id: 'requirements', label: 'Requirements', icon: 'FileText' }
  ];

  const activeSites = sites.filter(s => s.is_active).length;
  const openRequirements = requirements.filter(r => r.status === 'open' || r.status === 'in_progress').length;

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-background rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={onClose}>
                <Icon name="ChevronLeft" size={20} />
              </Button>
              <div>
                <h2 className="text-2xl font-bold text-foreground">{clientData?.company_name}</h2>
                <p className="text-sm text-muted-foreground">{clientData?.industry || 'No industry specified'}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleEditClient}>
                <Icon name="Edit" size={16} className="mr-2" />
                Edit Client
              </Button>
              <Button variant="ghost" size="icon" onClick={onClose} title="Close">
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="p-6 border-b border-border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Sites</p>
                  <p className="text-2xl font-bold text-foreground">{sites.length}</p>
                  <p className="text-xs text-muted-foreground mt-1">{activeSites} active</p>
                </div>
                <Icon name="MapPin" size={32} className="text-primary opacity-50" />
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Requirements</p>
                  <p className="text-2xl font-bold text-foreground">{requirements.length}</p>
                  <p className="text-xs text-muted-foreground mt-1">{openRequirements} open</p>
                </div>
                <Icon name="FileText" size={32} className="text-primary opacity-50" />
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <p className="text-lg font-semibold text-foreground capitalize">{clientData?.status || 'active'}</p>
                  <p className="text-xs text-muted-foreground mt-1">Client status</p>
                </div>
                <Icon name="CheckCircle" size={32} className="text-primary opacity-50" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex gap-1 px-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon name={tab.icon} size={16} />
                  {tab.label}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Company Information</h3>
                  <div className="space-y-3">
                    {clientData?.contact_person && (
                      <div className="flex items-center gap-3">
                        <Icon name="User" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Contact Person</p>
                          <p className="text-foreground">{clientData.contact_person}</p>
                        </div>
                      </div>
                    )}
                    {clientData?.email && (
                      <div className="flex items-center gap-3">
                        <Icon name="Mail" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Email</p>
                          <p className="text-foreground">{clientData.email}</p>
                        </div>
                      </div>
                    )}
                    {clientData?.phone && (
                      <div className="flex items-center gap-3">
                        <Icon name="Phone" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Phone</p>
                          <p className="text-foreground">{clientData.phone}</p>
                        </div>
                      </div>
                    )}
                    {clientData?.gst_number && (
                      <div className="flex items-center gap-3">
                        <Icon name="FileText" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">GST Number</p>
                          <p className="text-foreground">{clientData.gst_number}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Address</h3>
                  {clientData?.address ? (
                    <div className="flex items-start gap-3">
                      <Icon name="MapPin" size={16} className="text-muted-foreground mt-1" />
                      <p className="text-foreground whitespace-pre-line">{clientData.address}</p>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">No address provided</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sites' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Client Sites</h3>
                <Button onClick={handleAddSite} iconName="Plus" iconPosition="left" size="sm">
                  Add Site
                </Button>
              </div>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
                  <p className="text-muted-foreground">Loading sites...</p>
                </div>
              ) : sites.length === 0 ? (
                <div className="text-center py-12 bg-card border border-border rounded-lg">
                  <Icon name="MapPin" size={48} className="mx-auto mb-4 opacity-50 text-muted-foreground" />
                  <p className="text-muted-foreground">No sites found</p>
                  <Button onClick={handleAddSite} variant="outline" className="mt-4">
                    Add First Site
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sites.map(site => (
                    <div key={site.id} className="bg-card border border-border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-foreground">{site.site_name}</h4>
                          <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(site.is_active ? 'active' : 'inactive')}`}>
                            {site.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEditSite(site)}>
                            <Icon name="Edit" size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteSite(site.id)} className="text-destructive">
                            <Icon name="Trash2" size={14} />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        {site.address && (
                          <div className="flex items-start gap-2 text-muted-foreground">
                            <Icon name="MapPin" size={14} className="mt-0.5" />
                            <span className="line-clamp-2">{site.address}</span>
                          </div>
                        )}
                        {site.city && site.state && (
                          <div className="text-muted-foreground">
                            {site.city}, {site.state} {site.pincode && `- ${site.pincode}`}
                          </div>
                        )}
                        {site.contact_person && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Icon name="User" size={14} />
                            <span>{site.contact_person}</span>
                          </div>
                        )}
                        {site.phone && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Icon name="Phone" size={14} />
                            <span>{site.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'requirements' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Client Requirements</h3>
                <Button onClick={handleAddRequirement} iconName="Plus" iconPosition="left" size="sm">
                  Create Requirement
                </Button>
              </div>
              {requirements.length === 0 ? (
                <div className="text-center py-12 bg-card border border-border rounded-lg">
                  <Icon name="FileText" size={48} className="mx-auto mb-4 opacity-50 text-muted-foreground" />
                  <p className="text-muted-foreground">No requirements found</p>
                  <Button onClick={handleAddRequirement} variant="outline" className="mt-4">
                    Create First Requirement
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {requirements.map(req => (
                    <div key={req.id} className="bg-card border border-border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-foreground">{req.title}</h4>
                          {req.position_title && (
                            <p className="text-sm text-muted-foreground mt-1">{req.position_title}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(req.status)}`}>
                            {req.status.replace('_', ' ')}
                          </span>
                          {req.priority && (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              req.priority === 'urgent' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' :
                              req.priority === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300' :
                              'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                            }`}>
                              {req.priority}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        {req.quantity_needed && (
                          <div>
                            <p className="text-muted-foreground">Quantity</p>
                            <p className="font-medium text-foreground">{req.quantity_needed}</p>
                          </div>
                        )}
                        {req.location && (
                          <div>
                            <p className="text-muted-foreground">Location</p>
                            <p className="font-medium text-foreground">{req.location}</p>
                          </div>
                        )}
                        {req.deadline && (
                          <div>
                            <p className="text-muted-foreground">Deadline</p>
                            <p className="font-medium text-foreground">{new Date(req.deadline).toLocaleDateString()}</p>
                          </div>
                        )}
                        {req.experience_level && (
                          <div>
                            <p className="text-muted-foreground">Experience</p>
                            <p className="font-medium text-foreground">{req.experience_level}</p>
                          </div>
                        )}
                      </div>
                      {req.required_skills && Array.isArray(req.required_skills) && req.required_skills.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-border">
                          <p className="text-xs text-muted-foreground mb-1">Required Skills</p>
                          <div className="flex flex-wrap gap-1">
                            {req.required_skills.map((skill, idx) => (
                              <span key={idx} className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AddClientModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        client={clientData}
        onSave={handleClientSaved}
      />

      <AddClientSiteModal
        isOpen={showSiteModal}
        onClose={() => {
          setShowSiteModal(false);
          setEditingSite(null);
        }}
        site={editingSite}
        clients={clients.filter(c => c.id === client.id)}
        onSave={handleSiteSaved}
      />

      <CreateRequirementModal
        isOpen={showRequirementModal}
        onClose={() => setShowRequirementModal(false)}
        onRequirementCreated={handleRequirementCreated}
        preSelectedClientId={client.id}
      />
    </div>
  );
};

export default ClientDetailView;

